import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime } from "@/utils/dateUtils";
import { whatsappApi } from "@/services/whatsapp-api";
import type { WhatsAppContatoIgnorado } from "@/types/whatsapp";
import { toUserError } from "@/lib/api-error";

export function WhatsAppIgnorados() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const ignoradosQuery = useQuery({
    queryKey: ["whatsapp-contatos-ignorados"],
    queryFn: () => whatsappApi.listContatosIgnorados(),
  });

  const reativar = useMutation({
    mutationFn: (contato: WhatsAppContatoIgnorado) =>
      whatsappApi.reativarContato(contato.id),
    onSuccess: () => {
      toast({
        title: "Contato reativado",
        description: "Ele volta a ser capturado na próxima sincronização.",
      });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-contatos-ignorados"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-resumo"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao reativar",
        description: toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const contatos = ignoradosQuery.data?.contatos ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        {ignoradosQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : contatos.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Nenhum contato ignorado. Use "Ignorar contato" na revisão pra parar
            de capturar fornecedores e outros contatos que não são freelancers.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contato</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Ignorado em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contatos.map((contato) => (
                <TableRow key={contato.id}>
                  <TableCell className="font-medium">
                    {contato.contact_name ?? contato.freelancer_nome ?? "Sem nome"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {contato.phone_number ?? "-"}
                  </TableCell>
                  <TableCell>{contato.ignore_reason ?? "-"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(contato.ignored_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => reativar.mutate(contato)}
                      disabled={reativar.isPending}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reativar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
